import type { CategoryNode, ProductCategoryChain } from "./api";

/**
 * A product's place in the catalogue, shallowest first:
 * Company › Main category › Category › Sub category.
 *
 * The API returns the chain deepest-first, each node holding its parent, so
 * this walks up and reverses. Derived from the product rather than chosen on
 * the way to it - which is the whole point of searching by model number.
 */
export function hierarchyOf(
  // Anything carrying a category chain: a Product from the catalogue, or a
  // ServiceJob now that a collected battery is categorised too.
  subject: { category?: ProductCategoryChain | null },
): { level: number; name: string }[] {
  const chain: { level: number; name: string }[] = [];
  let node: ProductCategoryChain | null | undefined = subject.category;
  while (node) {
    chain.push({ level: node.level, name: node.name });
    node = node.parent;
  }
  return chain.reverse();
}

/** "AMARON › BATTERY › 4W › FLO". Empty when the API sent no chain. */
export function hierarchyPath(subject: {
  category?: ProductCategoryChain | null;
}): string {
  return hierarchyOf(subject)
    .map((node) => node.name)
    .join(" › ");
}

/*
 * levelLabel used to live here: it lower-cased an identifier ("SUB_CATEGORY")
 * to produce the words on screen ("Sub category").
 *
 * It is gone, and its removal is the point of this rename rather than a side
 * effect. Deriving the label from the identifier tied what a user reads to
 * what a constant is spelled, so the business renaming a level either renamed
 * the constant too or the screen kept the old word. Worse here than usual,
 * because level 4's NEW name is level 3's OLD one - the derivation would have
 * produced a plausible, wrong answer.
 *
 * The server states the label now (categoryLevelLabel), and every screen
 * renders what it is given.
 */

/**
 * The options to offer at each level of a cascade, given the tree and the
 * path chosen so far: the roots, then the children of whatever is selected,
 * stopping at the first level with nothing chosen.
 *
 * The order picker's browse fallback, for somebody who does not know the
 * model number. The battery-collection screen used to share it, and does not
 * any more: a collected battery is identified by its MODEL now, and the four
 * levels are derived from the product rather than worked down by hand.
 */
export function cascadeLevels(
  roots: CategoryNode[],
  path: readonly string[],
): { options: CategoryNode[]; selectedId: string }[] {
  const result: { options: CategoryNode[]; selectedId: string }[] = [];
  let options = roots;
  for (let depth = 0; options.length > 0; depth += 1) {
    const selectedId = path[depth] ?? "";
    result.push({ options, selectedId });
    const selectedNode = options.find((node) => node.id === selectedId);
    if (!selectedNode) break;
    options = selectedNode.children;
  }
  return result;
}

/** Every leaf of a category tree, with the path that reaches it. */
export function categoryLeaves(
  nodes: CategoryNode[],
  trail: string[] = [],
): { id: string; path: string }[] {
  return nodes.flatMap((node) =>
    node.children.length > 0
      ? categoryLeaves(node.children, [...trail, node.name])
      : [{ id: node.id, path: [...trail, node.name].join(" › ") }],
  );
}
